import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useEffect, useRef, useState } from "react";
import { getFormByFormID } from "./fetchData";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Image } from "primereact/image";

const ViewConfinedSpacesEquipment = ({ attachedFormData }: any) => {
  const query = new URLSearchParams(window.location.search);
  const formIDParam = Number(query.get("formID"));
  const ImageNotAvailable = process.env.REACT_APP_IMAGE_NOT_FOUND_URL || "";

  const icon = (
    <i
      className="pi pi-eye tw-text-white tw-h-full tw-w-full tw-justify-center tw-items-center tw-flex"
      style={{
        backgroundColor: "black",
        opacity: 0.5,
        transition: "opacity 0.3s ease",
      }}
    ></i>
  );

  const toast = useRef<Toast>(null);
  const [formData, setFormData] = useState<any>(attachedFormData ?? null);
  const [formDataLoading, setFormDataLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!attachedFormData) {
      if (!formIDParam) {
        navigate("/layout/health-and-safety/pre-use-confined-space-equipment");
      }
      getFormByFormID(
        setFormData,
        setFormDataLoading,
        formIDParam,
        navigate,
        toast
      );
    }
  }, []);

  // Format date and time from timestamp
  const formatDate = (timestamp: number) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper to get equipment photo by label
  const getEquipmentPhoto = (label: string) => {
    const photo =
      formData?.fileJson?.equipmentPhotos &&
      formData?.fileJson?.equipmentPhotos?.find((img: any) =>
        img.originalname
          .replace(".png", "")
          .toLowerCase()
          .includes(label.toLowerCase())
      );
    return photo?.file || ImageNotAvailable;
  };

  // Helper to get timestamp and location by photoKey
  const getPhotoMeta = (photoKey: string) => {
    const locationObj = formData?.timeStampLocationJson?.locations?.find(
      (loc: any) => loc.photoKey === photoKey
    );
    const timeObj = formData?.timeStampLocationJson?.timeStamps?.find(
      (t: any) => t.photoKey === photoKey
    );
    return {
      address: locationObj?.address || "",
      timestamp: timeObj?.timestamp || "",
    };
  };

  return (
    <React.Fragment>
      {formDataLoading ? (
        <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-[50px] lg:tw-left-[120px] tw-z-50">
          <ProgressSpinner />
        </div>
      ) : (
        <div className="form-border">
          {!attachedFormData && (
            <div className="form-header-border">
              <h2 className="form-header-h2">
                View Confined Spaces Equipment Checklist
              </h2>
            </div>
          )}
          <div>
            <div className="tw-grid tw-grid-cols-1  2xl:tw-grid-cols-3 tw-gap-6 tw-px-4 tw-py-4">
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <p className="leak-detail-label">Site</p>
                <p className="leak-value">
                  {formData?.formJson?.site || "N/A"}
                </p>
              </div>
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <p className="leak-detail-label">Completed By</p>
                <p className="leak-value">
                  {formData?.formJson?.completedBy?.displayName || "N/A"}
                </p>
              </div>
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <p className="leak-detail-label">Date</p>
                <p className="leak-value">
                  {formatDate(formData?.formJson?.date) || "N/A"}
                </p>
              </div>
            </div>
            <div className="tw-mt-6">
              <DataTable
                value={formData?.formJson?.tripodChecklist || []}
                className="p-datatable-striped tw-mt-6"
                showGridlines
              >
                <Column field="label" header="TRIPOD" />
                <Column
                  header="Action"
                  body={(rowData) =>
                    rowData.value === "yes" ? (
                      <span>Yes</span>
                    ) : rowData.value === "no" ? (
                      <span>No</span>
                    ) : (
                      <span>N/A</span>
                    )
                  }
                />
              </DataTable>
              <DataTable
                value={formData?.formJson?.winchChecklist || []}
                className="p-datatable-striped tw-mt-6"
                showGridlines
              >
                <Column field="label" header="WINCH" />
                <Column
                  header="Action"
                  body={(rowData) =>
                    rowData.value === "yes" ? (
                      <span>Yes</span>
                    ) : rowData.value === "no" ? (
                      <span>No</span>
                    ) : (
                      <span>N/A</span>
                    )
                  }
                />
              </DataTable>
              <DataTable
                value={formData?.formJson?.safetyHarnessChecklist || []}
                className="p-datatable-striped tw-mt-6"
                showGridlines
              >
                <Column field="label" header="SAFETY HARNESSES  " />
                <Column
                  header="Action"
                  body={(rowData) =>
                    rowData.value === "yes" ? (
                      <span>Yes</span>
                    ) : rowData.value === "no" ? (
                      <span>No</span>
                    ) : (
                      <span>N/A</span>
                    )
                  }
                />
              </DataTable>
              <DataTable
                value={formData?.formJson?.connectorsChecklist || []}
                className="p-datatable-striped tw-mt-6"
                showGridlines
              >
                <Column
                  field="label"
                  header="CONNECTORS – INCLUDING KARABINERS, SNAP HOOKS AND TUBE NUT ETC."
                />
                <Column
                  header="Action"
                  body={(rowData) =>
                    rowData.value === "yes" ? (
                      <span>Yes</span>
                    ) : rowData.value === "no" ? (
                      <span>No</span>
                    ) : (
                      <span>N/A</span>
                    )
                  }
                />
              </DataTable>
              <DataTable
                value={formData?.formJson?.gasMonitorChecklist || []}
                className="p-datatable-striped tw-mt-6"
                showGridlines
              >
                <Column field="label" header="GAS MONITOR " />
                <Column
                  header="Action"
                  body={(rowData) =>
                    rowData.value === "yes" ? (
                      <span>Yes</span>
                    ) : rowData.value === "no" ? (
                      <span>No</span>
                    ) : (
                      <span>N/A</span>
                    )
                  }
                />
              </DataTable>
            </div>
            <div className="tw-pt-4 tw-pb-2 tw-border-b-[1px] tw-border-[#E2E8F0] tw-mt-4 tw-px-4 ">
              <h2 className="tw-text-lg tw-font-semibold tw-text-[#101828]">
                Pictures of equipment with their calibration date
              </h2>
            </div>
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-6 tw-px-4 tw-py-4 ">
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-2 ">
                <p className="leak-detail-label">Tripos</p>
                <div>
                  <Image
                    src={getEquipmentPhoto("tripos")}
                    preview
                    alt={`Vehicle Photograph Tripos`}
                    className="tw-w-32 tw-shrink-0 tw-object-contain"
                    indicatorIcon={icon}
                    loading="lazy"
                    // downloadable
                    pt={{
                      rotateRightButton: { className: "tw-text-white" },
                      downloadButton: { className: "tw-text-white" },
                      rotateLeftButton: { className: "tw-text-white" },
                      zoomInButton: { className: "tw-text-white" },
                      zoomOutButton: { className: "tw-text-white" },
                      closeButton: { className: "tw-text-white" },
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = ImageNotAvailable;
                    }}
                  />
                  {(getPhotoMeta("tripos").timestamp ||
                    getPhotoMeta("tripos").address) && (
                    <div className="tw-mt-2">
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("tripos").address}
                      </p>
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("tripos").timestamp}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-2 ">
                <p className="leak-detail-label">Winch</p>
                <div>
                  <Image
                    src={getEquipmentPhoto("winch")}
                    preview
                    alt={`Vehicle Photograph Winch`}
                    className="tw-w-32 tw-shrink-0 tw-object-contain"
                    indicatorIcon={icon}
                    loading="lazy"
                    // downloadable
                    pt={{
                      rotateRightButton: { className: "tw-text-white" },
                      downloadButton: { className: "tw-text-white" },
                      rotateLeftButton: { className: "tw-text-white" },
                      zoomInButton: { className: "tw-text-white" },
                      zoomOutButton: { className: "tw-text-white" },
                      closeButton: { className: "tw-text-white" },
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = ImageNotAvailable;
                    }}
                  />
                  {(getPhotoMeta("winch").timestamp ||
                    getPhotoMeta("winch").address) && (
                    <div className="tw-mt-2">
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("winch").address}
                      </p>
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("winch").timestamp}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-2 ">
                <p className="leak-detail-label">Harness</p>
                <div>
                  <Image
                    src={getEquipmentPhoto("harness")}
                    preview
                    alt={`Vehicle Photograph Harness`}
                    className="tw-w-32 tw-shrink-0 tw-object-contain"
                    indicatorIcon={icon}
                    loading="lazy"
                    // downloadable
                    pt={{
                      rotateRightButton: { className: "tw-text-white" },
                      downloadButton: { className: "tw-text-white" },
                      rotateLeftButton: { className: "tw-text-white" },
                      zoomInButton: { className: "tw-text-white" },
                      zoomOutButton: { className: "tw-text-white" },
                      closeButton: { className: "tw-text-white" },
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = ImageNotAvailable;
                    }}
                  />
                  {(getPhotoMeta("harness").timestamp ||
                    getPhotoMeta("harness").address) && (
                    <div className="tw-mt-2">
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("harness").address}
                      </p>
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("harness").timestamp}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-2 ">
                <p className="leak-detail-label">Gas Monitors</p>
                <div>
                  <Image
                    src={getEquipmentPhoto("gas_monitors")}
                    preview
                    alt={`Vehicle Photograph Gas Monitors`}
                    className="tw-w-32 tw-shrink-0 tw-object-contain"
                    indicatorIcon={icon}
                    loading="lazy"
                    // downloadable
                    pt={{
                      rotateRightButton: { className: "tw-text-white" },
                      downloadButton: { className: "tw-text-white" },
                      rotateLeftButton: { className: "tw-text-white" },
                      zoomInButton: { className: "tw-text-white" },
                      zoomOutButton: { className: "tw-text-white" },
                      closeButton: { className: "tw-text-white" },
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = ImageNotAvailable;
                    }}
                  />
                  {(getPhotoMeta("gas_monitors").timestamp ||
                    getPhotoMeta("gas_monitors").address) && (
                    <div className="tw-mt-2">
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("gas_monitors").address}
                      </p>
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("gas_monitors").timestamp}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-2 ">
                <p className="leak-detail-label">Connectors </p>
                <div>
                  <Image
                    src={getEquipmentPhoto("connectors")}
                    preview
                    alt={`Vehicle Photograph Connectors`}
                    className="tw-w-32 tw-shrink-0 tw-object-contain"
                    indicatorIcon={icon}
                    loading="lazy"
                    // downloadable
                    pt={{
                      rotateRightButton: { className: "tw-text-white" },
                      downloadButton: { className: "tw-text-white" },
                      rotateLeftButton: { className: "tw-text-white" },
                      zoomInButton: { className: "tw-text-white" },
                      zoomOutButton: { className: "tw-text-white" },
                      closeButton: { className: "tw-text-white" },
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = ImageNotAvailable;
                    }}
                  />
                  {(getPhotoMeta("connectors").timestamp ||
                    getPhotoMeta("connectors").address) && (
                    <div className="tw-mt-2">
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("connectors").address}
                      </p>
                      <p className="tw-text-sm tw-text-gray-900">
                        {getPhotoMeta("connectors").timestamp}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!attachedFormData && (
              <div className="tw-flex tw-justify-end tw-items-center tw-p-4  tw-gap-2 !tw-border-t-[#E2E8F0] tw-border-t-[1px]">
                <Button
                  className="form-cancel-button"
                  label="Cancel"
                  onClick={() =>
                    navigate(
                      "/layout/health-and-safety/pre-use-confined-space-equipment"
                    )
                  }
                  color="white"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default ViewConfinedSpacesEquipment;
